import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import AdminLayout from "@/components/AdminLayout";
import { formatINR, formatDate } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle 
} from "@/components/ui/dialog";
import { 
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger
} from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Save, RefreshCw, Plus, Pencil, Trash2, Clock, User, Sparkles, ChevronDown, ChevronRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminPricingRules = () => {
  const { getToken } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Venue Pricing
  const [venuePricing, setVenuePricing] = useState(null);
  
  // Payment Modes
  const [paymentModes, setPaymentModes] = useState([]);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [editingPaymentMode, setEditingPaymentMode] = useState(null);
  const [paymentForm, setPaymentForm] = useState({
    name: "",
    advance_percent: 50,
    discount_percent: 10,
    description: "",
    is_active: true,
    sort_order: 0
  });
  
  // Food Tiers
  const [foodTiers, setFoodTiers] = useState([]);
  const [showFoodModal, setShowFoodModal] = useState(false);
  const [editingFoodTier, setEditingFoodTier] = useState(null);
  const [foodForm, setFoodForm] = useState({
    name: "",
    min_guarantee: 50,
    base_rate: 3000,
    additional_rate: 2000,
    is_active: true,
    sort_order: 0
  });

  // Decor Categories & Items
  const [decorCategories, setDecorCategories] = useState([]);
  const [decorItems, setDecorItems] = useState([]);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryForm, setCategoryForm] = useState({
    name: "",
    sort_order: 0,
    is_active: true
  });
  const [showDecorItemModal, setShowDecorItemModal] = useState(false);
  const [editingDecorItem, setEditingDecorItem] = useState(null);
  const [decorItemForm, setDecorItemForm] = useState({
    category_id: "",
    category_name: "",
    item_name: "",
    tariff: 0,
    is_active: true,
    sort_order: 0
  });

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    try {
      const [venueRes, paymentRes, foodRes, categoriesRes, itemsRes] = await Promise.all([
        axios.get(`${API}/venue-pricing`),
        axios.get(`${API}/payment-modes`),
        axios.get(`${API}/food-tiers`),
        axios.get(`${API}/decor-categories`),
        axios.get(`${API}/decor-items`)
      ]);
      setVenuePricing(venueRes.data);
      setPaymentModes(paymentRes.data);
      setFoodTiers(foodRes.data);
      setDecorCategories(categoriesRes.data);
      setDecorItems(itemsRes.data);
    } catch (error) {
      toast.error("Failed to load pricing data");
    } finally {
      setLoading(false);
    }
  };

  // === VENUE PRICING ===
  const handleVenueChange = (field, value) => {
    const numValue = parseFloat(value) || 0;
    if (numValue < 0) {
      toast.error("Value cannot be negative");
      return;
    }
    setVenuePricing(prev => ({
      ...prev,
      [field]: numValue
    }));
  };

  const saveVenuePricing = async () => {
    setSaving(true);
    try {
      const response = await axios.put(`${API}/venue-pricing`, venuePricing, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setVenuePricing(response.data);
      toast.success("Venue pricing saved successfully");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  // === PAYMENT MODES ===
  const openPaymentModal = (mode = null) => {
    if (mode) {
      setEditingPaymentMode(mode);
      setPaymentForm({
        name: mode.name,
        advance_percent: mode.advance_percent,
        discount_percent: mode.discount_percent,
        description: mode.description || "",
        is_active: mode.is_active,
        sort_order: mode.sort_order
      });
    } else {
      setEditingPaymentMode(null);
      setPaymentForm({
        name: "",
        advance_percent: 50,
        discount_percent: 10,
        description: "",
        is_active: true,
        sort_order: paymentModes.length
      });
    }
    setShowPaymentModal(true);
  };

  const savePaymentMode = async (e) => {
    e.preventDefault();
    
    if (paymentForm.discount_percent < 0 || paymentForm.discount_percent > 100) {
      toast.error("Discount must be between 0 and 100%");
      return;
    }
    
    try {
      if (editingPaymentMode) {
        await axios.put(`${API}/payment-modes/${editingPaymentMode.id}`, paymentForm, {
          headers: { Authorization: `Bearer ${getToken()}` }
        });
        toast.success("Payment mode updated");
      } else {
        await axios.post(`${API}/payment-modes`, paymentForm, {
          headers: { Authorization: `Bearer ${getToken()}` }
        });
        toast.success("Payment mode created");
      }
      setShowPaymentModal(false);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save");
    }
  };

  const deletePaymentMode = async (id) => {
    if (!window.confirm("Delete this payment mode?")) return;
    try {
      await axios.delete(`${API}/payment-modes/${id}`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      toast.success("Payment mode deleted");
      fetchAllData();
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  // === FOOD TIERS ===
  const openFoodModal = (tier = null) => {
    if (tier) {
      setEditingFoodTier(tier);
      setFoodForm({
        name: tier.name,
        min_guarantee: tier.min_guarantee,
        base_rate: tier.base_rate,
        additional_rate: tier.additional_rate,
        is_active: tier.is_active,
        sort_order: tier.sort_order
      });
    } else {
      setEditingFoodTier(null);
      setFoodForm({
        name: "",
        min_guarantee: 50,
        base_rate: 3000,
        additional_rate: 2000,
        is_active: true,
        sort_order: foodTiers.length
      });
    }
    setShowFoodModal(true);
  };

  const saveFoodTier = async (e) => {
    e.preventDefault();
    
    if (foodForm.base_rate < 0 || foodForm.additional_rate < 0 || foodForm.min_guarantee < 0) {
      toast.error("Values cannot be negative");
      return;
    }
    
    try {
      if (editingFoodTier) {
        await axios.put(`${API}/food-tiers/${editingFoodTier.id}`, foodForm, {
          headers: { Authorization: `Bearer ${getToken()}` }
        });
        toast.success("Food tier updated");
      } else {
        await axios.post(`${API}/food-tiers`, foodForm, {
          headers: { Authorization: `Bearer ${getToken()}` }
        });
        toast.success("Food tier created");
      }
      setShowFoodModal(false);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save");
    }
  };

  const deleteFoodTier = async (id) => {
    if (!window.confirm("Delete this food tier?")) return;
    try {
      await axios.delete(`${API}/food-tiers/${id}`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      toast.success("Food tier deleted");
      fetchAllData();
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  // === DECOR CATEGORIES ===
  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  const openCategoryModal = (category = null) => {
    if (category) {
      setEditingCategory(category);
      setCategoryForm({
        name: category.name,
        sort_order: category.sort_order,
        is_active: category.is_active
      });
    } else {
      setEditingCategory(null);
      setCategoryForm({
        name: "",
        sort_order: decorCategories.length,
        is_active: true
      });
    }
    setShowCategoryModal(true);
  };

  const saveCategory = async (e) => {
    e.preventDefault();
    try {
      if (editingCategory) {
        await axios.put(`${API}/decor-categories/${editingCategory.id}`, categoryForm, {
          headers: { Authorization: `Bearer ${getToken()}` }
        });
        toast.success("Category updated");
      } else {
        await axios.post(`${API}/decor-categories`, categoryForm, {
          headers: { Authorization: `Bearer ${getToken()}` }
        });
        toast.success("Category created");
      }
      setShowCategoryModal(false);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save");
    }
  };

  const deleteCategory = async (id) => {
    if (!window.confirm("Delete this category and all its items?")) return;
    try {
      await axios.delete(`${API}/decor-categories/${id}`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      toast.success("Category deleted");
      fetchAllData();
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  // === DECOR ITEMS ===
  const openDecorItemModal = (item = null, presetCategoryId = null) => {
    if (item) {
      setEditingDecorItem(item);
      setDecorItemForm({
        category_id: item.category_id,
        category_name: item.category_name,
        item_name: item.item_name,
        tariff: item.tariff,
        is_active: item.is_active,
        sort_order: item.sort_order
      });
    } else {
      setEditingDecorItem(null);
      const category = presetCategoryId ? decorCategories.find(c => c.id === presetCategoryId) : decorCategories[0];
      setDecorItemForm({
        category_id: category?.id || "",
        category_name: category?.name || "",
        item_name: "",
        tariff: 0,
        is_active: true,
        sort_order: decorItems.filter(i => i.category_id === (presetCategoryId || category?.id)).length
      });
    }
    setShowDecorItemModal(true);
  };

  const handleDecorCategoryChange = (categoryId) => {
    const category = decorCategories.find(c => c.id === categoryId);
    setDecorItemForm(prev => ({
      ...prev,
      category_id: categoryId,
      category_name: category?.name || ""
    }));
  };

  const saveDecorItem = async (e) => {
    e.preventDefault();
    if (decorItemForm.tariff < 0) {
      toast.error("Tariff cannot be negative");
      return;
    }
    try {
      if (editingDecorItem) {
        await axios.put(`${API}/decor-items/${editingDecorItem.id}`, decorItemForm, {
          headers: { Authorization: `Bearer ${getToken()}` }
        });
        toast.success("Decor item updated");
      } else {
        await axios.post(`${API}/decor-items`, decorItemForm, {
          headers: { Authorization: `Bearer ${getToken()}` }
        });
        toast.success("Decor item created");
      }
      setShowDecorItemModal(false);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save");
    }
  };

  const deleteDecorItem = async (id) => {
    if (!window.confirm("Delete this decor item?")) return;
    try {
      await axios.delete(`${API}/decor-items/${id}`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      toast.success("Decor item deleted");
      fetchAllData();
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  const getItemsByCategory = (categoryId) => {
    return decorItems.filter(item => item.category_id === categoryId);
  };

  if (loading) {
    return (
      <AdminLayout title="Pricing & Rules">
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-[#B08968]" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Pricing & Rules">
      <div className="space-y-6" data-testid="pricing-rules-page">
        <p className="text-muted-foreground">
          Central pricing settings. All changes reflect immediately on the frontend.
        </p>

        <Tabs defaultValue="venue" className="w-full">
          <TabsList className="mb-6">
            <TabsTrigger value="venue">Venue Pricing</TabsTrigger>
            <TabsTrigger value="payment">Payment Discounts</TabsTrigger>
            <TabsTrigger value="food">Food Tiers</TabsTrigger>
            <TabsTrigger value="decor">Decor Catalog</TabsTrigger>
          </TabsList>

          {/* VENUE PRICING TAB */}
          <TabsContent value="venue">
            <div className="space-y-6">
              {/* Base Prices */}
              <div className="bg-white rounded-2xl border border-border/40 p-6 shadow-elegant">
                <h3 className="text-lg font-medium mb-2">Base Venue Prices</h3>
                <p className="text-sm text-muted-foreground mb-6">
                  Set prices inclusive of GST. If GST needs to be applied separately, set GST % below.
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="form-group">
                    <Label>24 Hours Package (₹)</Label>
                    <Input
                      type="number"
                      data-testid="price-24hr-input"
                      value={venuePricing?.price_24hr || 0}
                      onChange={(e) => handleVenueChange('price_24hr', e.target.value)}
                      min={0}
                    />
                  </div>
                  <div className="form-group">
                    <Label>48 Hours Package (₹)</Label>
                    <Input
                      type="number"
                      data-testid="price-48hr-input"
                      value={venuePricing?.price_48hr || 0}
                      onChange={(e) => handleVenueChange('price_48hr', e.target.value)}
                      min={0}
                    />
                  </div>
                  <div className="form-group">
                    <Label>72 Hours Package (₹)</Label>
                    <Input
                      type="number"
                      data-testid="price-72hr-input"
                      value={venuePricing?.price_72hr || 0}
                      onChange={(e) => handleVenueChange('price_72hr', e.target.value)}
                      min={0}
                    />
                  </div>
                </div>
              </div>

              {/* GST Settings */}
              <div className="bg-white rounded-2xl border border-border/40 p-6 shadow-elegant">
                <h3 className="text-lg font-medium mb-2">GST Configuration</h3>
                <p className="text-sm text-muted-foreground mb-6">
                  Set to 0 if prices already include GST. Otherwise, GST will be applied on venue base price.
                </p>
                
                <div className="max-w-xs">
                  <div className="form-group">
                    <Label>GST Percentage (%)</Label>
                    <Input
                      type="number"
                      data-testid="gst-percent-input"
                      value={venuePricing?.gst_percent || 0}
                      onChange={(e) => handleVenueChange('gst_percent', e.target.value)}
                      min={0}
                      max={100}
                    />
                  </div>
                </div>
              </div>

              {/* Accommodation Rules */}
              <div className="bg-white rounded-2xl border border-border/40 p-6 shadow-elegant">
                <h3 className="text-lg font-medium mb-2">Accommodation Rules</h3>
                <p className="text-sm text-muted-foreground mb-6">
                  Extra charges apply for guests beyond the included count.
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="form-group">
                    <Label>Included Pax (Base)</Label>
                    <Input
                      type="number"
                      data-testid="included-pax-input"
                      value={venuePricing?.included_pax || 60}
                      onChange={(e) => handleVenueChange('included_pax', e.target.value)}
                      min={0}
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Number of guests included in base price
                    </p>
                  </div>
                  <div className="form-group">
                    <Label>Extra Pax Charge (₹/person/day)</Label>
                    <Input
                      type="number"
                      data-testid="extra-pax-charge-input"
                      value={venuePricing?.extra_pax_charge_per_day || 1000}
                      onChange={(e) => handleVenueChange('extra_pax_charge_per_day', e.target.value)}
                      min={0}
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Per person per day for guests above included count
                    </p>
                  </div>
                </div>
              </div>

              {/* Audit Info */}
              {venuePricing?.updated_at && (
                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    Last updated: {formatDate(venuePricing.updated_at)}
                  </span>
                  {venuePricing.updated_by && (
                    <span className="flex items-center gap-1">
                      <User className="w-4 h-4" />
                      By: {venuePricing.updated_by}
                    </span>
                  )}
                </div>
              )}

              <div className="flex justify-end">
                <Button
                  onClick={saveVenuePricing}
                  disabled={saving}
                  data-testid="save-venue-btn"
                  className="bg-[#B08968] hover:bg-[#9A7558] text-white px-8"
                >
                  {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Save Venue Pricing
                </Button>
              </div>
            </div>
          </TabsContent>

          {/* PAYMENT DISCOUNTS TAB */}
          <TabsContent value="payment">
            <div className="space-y-6">
              <div className="bg-white rounded-2xl border border-border/40 p-6 shadow-elegant">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-lg font-medium">Payment Mode Discounts</h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      Discount applies on Total Deal Value (Venue + Food + Extra Stay)
                    </p>
                  </div>
                  <Button
                    onClick={() => openPaymentModal()}
                    data-testid="add-payment-mode-btn"
                    className="bg-[#B08968] hover:bg-[#9A7558] text-white"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Mode
                  </Button>
                </div>

                <div className="space-y-3">
                  {paymentModes.map((mode) => (
                    <div 
                      key={mode.id}
                      data-testid={`payment-mode-${mode.id}`}
                      className={`flex items-center justify-between p-4 rounded-xl border ${mode.is_active ? 'bg-white' : 'bg-gray-50 opacity-60'}`}
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-3">
                          <span className="font-medium">{mode.name}</span>
                          {!mode.is_active && (
                            <span className="text-xs bg-gray-200 px-2 py-0.5 rounded">Inactive</span>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">
                          {mode.advance_percent}% advance → <span className="font-semibold text-green-600">{mode.discount_percent}% discount</span>
                        </p>
                        {mode.description && (
                          <p className="text-xs text-muted-foreground mt-1">{mode.description}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => openPaymentModal(mode)}
                          className="p-2 hover:bg-secondary rounded-lg text-muted-foreground hover:text-[#B08968]"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => deletePaymentMode(mode.id)}
                          className="p-2 hover:bg-red-50 rounded-lg text-muted-foreground hover:text-red-600"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                {paymentModes.length === 0 && (
                  <p className="text-center py-8 text-muted-foreground">
                    No payment modes configured. Add one to get started.
                  </p>
                )}
              </div>
            </div>
          </TabsContent>

          {/* FOOD TIERS TAB */}
          <TabsContent value="food">
            <div className="space-y-6">
              <div className="bg-white rounded-2xl border border-border/40 p-6 shadow-elegant">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-lg font-medium">Food Pricing Tiers</h3>
                    <p className="text-sm text-amber-600 mt-1">
                      ⚠️ Rates are per head per day and auto-scale based on duration.
                    </p>
                  </div>
                  <Button
                    onClick={() => openFoodModal()}
                    data-testid="add-food-tier-btn"
                    className="bg-[#B08968] hover:bg-[#9A7558] text-white"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Tier
                  </Button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {foodTiers.map((tier) => (
                    <div 
                      key={tier.id}
                      data-testid={`food-tier-${tier.id}`}
                      className={`p-4 rounded-xl border ${tier.is_active ? 'bg-white' : 'bg-gray-50 opacity-60'}`}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <span className="font-medium">{tier.name}</span>
                          {!tier.is_active && (
                            <span className="ml-2 text-xs bg-gray-200 px-2 py-0.5 rounded">Inactive</span>
                          )}
                        </div>
                        <div className="flex gap-1">
                          <button
                            onClick={() => openFoodModal(tier)}
                            className="p-1.5 hover:bg-secondary rounded-lg text-muted-foreground hover:text-[#B08968]"
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => deleteFoodTier(tier.id)}
                            className="p-1.5 hover:bg-red-50 rounded-lg text-muted-foreground hover:text-red-600"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      <div className="space-y-1 text-sm">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Min Guarantee</span>
                          <span className="font-medium">{tier.min_guarantee} pax</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Base Rate</span>
                          <span className="font-medium text-[#B08968]">{formatINR(tier.base_rate)}/head/day</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Additional Rate</span>
                          <span className="font-medium">{formatINR(tier.additional_rate)}/head/day</span>
                        </div>
                      </div>
                      {tier.updated_at && (
                        <p className="text-xs text-muted-foreground mt-3 pt-2 border-t">
                          Updated: {formatDate(tier.updated_at)}
                        </p>
                      )}
                    </div>
                  ))}
                </div>

                {foodTiers.length === 0 && (
                  <p className="text-center py-8 text-muted-foreground">
                    No food tiers configured. Add one to get started.
                  </p>
                )}
              </div>
            </div>
          </TabsContent>

          {/* DECOR CATALOG TAB */}
          <TabsContent value="decor">
            <div className="space-y-6">
              <div className="bg-white rounded-2xl border border-border/40 p-6 shadow-elegant">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-lg font-medium">Decor Catalog</h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      Manage decor categories and items with their tariffs
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => openCategoryModal()}
                      variant="outline"
                      data-testid="add-category-btn"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add Category
                    </Button>
                    <Button
                      onClick={() => openDecorItemModal()}
                      data-testid="add-decor-item-btn"
                      className="bg-[#B08968] hover:bg-[#9A7558] text-white"
                      disabled={decorCategories.length === 0}
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add Item
                    </Button>
                  </div>
                </div>

                {decorCategories.length === 0 ? (
                  <div className="text-center py-12">
                    <Sparkles className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
                    <p className="text-muted-foreground">
                      No decor categories yet. Create a category to start adding items.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {decorCategories.map((category) => {
                      const items = getItemsByCategory(category.id);
                      const isExpanded = expandedCategories[category.id];
                      
                      return (
                        <div key={category.id} className="border rounded-xl overflow-hidden" data-testid={`decor-category-${category.id}`}>
                          {/* Category Header */}
                          <div 
                            className={`flex items-center justify-between p-4 cursor-pointer hover:bg-secondary/30 ${!category.is_active ? 'bg-gray-50 opacity-60' : 'bg-white'}`}
                            onClick={() => toggleCategory(category.id)}
                          >
                            <div className="flex items-center gap-3">
                              {isExpanded ? (
                                <ChevronDown className="w-5 h-5 text-muted-foreground" />
                              ) : (
                                <ChevronRight className="w-5 h-5 text-muted-foreground" />
                              )}
                              <span className="font-medium">{category.name}</span>
                              <span className="text-sm text-muted-foreground">({items.length} items)</span>
                              {!category.is_active && (
                                <span className="text-xs bg-gray-200 px-2 py-0.5 rounded">Inactive</span>
                              )}
                            </div>
                            <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                              <button
                                onClick={() => openDecorItemModal(null, category.id)}
                                className="p-2 hover:bg-secondary rounded-lg text-muted-foreground hover:text-green-600"
                                title="Add item to category"
                              >
                                <Plus className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => openCategoryModal(category)}
                                className="p-2 hover:bg-secondary rounded-lg text-muted-foreground hover:text-[#B08968]"
                              >
                                <Pencil className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => deleteCategory(category.id)}
                                className="p-2 hover:bg-red-50 rounded-lg text-muted-foreground hover:text-red-600"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                          
                          {/* Category Items */}
                          {isExpanded && items.length > 0 && (
                            <div className="border-t bg-secondary/10">
                              {items.map((item) => (
                                <div 
                                  key={item.id}
                                  data-testid={`decor-item-${item.id}`}
                                  className={`flex items-center justify-between px-6 py-3 border-b last:border-b-0 ${!item.is_active ? 'opacity-50' : ''}`}
                                >
                                  <div>
                                    <span className="font-medium">{item.item_name}</span>
                                    {!item.is_active && (
                                      <span className="ml-2 text-xs bg-gray-200 px-2 py-0.5 rounded">Inactive</span>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-4">
                                    <span className="font-semibold text-[#B08968]">{formatINR(item.tariff)}</span>
                                    <div className="flex gap-1">
                                      <button
                                        onClick={() => openDecorItemModal(item)}
                                        className="p-1.5 hover:bg-secondary rounded-lg text-muted-foreground hover:text-[#B08968]"
                                      >
                                        <Pencil className="w-4 h-4" />
                                      </button>
                                      <button
                                        onClick={() => deleteDecorItem(item.id)}
                                        className="p-1.5 hover:bg-red-50 rounded-lg text-muted-foreground hover:text-red-600"
                                      >
                                        <Trash2 className="w-4 h-4" />
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                          
                          {isExpanded && items.length === 0 && (
                            <div className="border-t bg-secondary/10 p-4 text-center text-sm text-muted-foreground">
                              No items in this category. 
                              <button 
                                onClick={() => openDecorItemModal(null, category.id)}
                                className="text-[#B08968] hover:underline ml-1"
                              >
                                Add one now
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Summary Stats */}
                {decorCategories.length > 0 && (
                  <div className="mt-6 pt-4 border-t flex gap-6 text-sm text-muted-foreground">
                    <span>{decorCategories.length} categories</span>
                    <span>{decorItems.length} total items</span>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Payment Mode Modal */}
      <Dialog open={showPaymentModal} onOpenChange={setShowPaymentModal}>
        <DialogContent data-testid="payment-mode-modal">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">
              {editingPaymentMode ? "Edit Payment Mode" : "Add Payment Mode"}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={savePaymentMode} className="space-y-4 mt-4">
            <div className="form-group">
              <Label>Mode Name</Label>
              <Input
                required
                value={paymentForm.name}
                onChange={(e) => setPaymentForm({...paymentForm, name: e.target.value})}
                placeholder="e.g., 50% advance"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="form-group">
                <Label>Advance Percent (%)</Label>
                <Input
                  type="number"
                  required
                  value={paymentForm.advance_percent}
                  onChange={(e) => setPaymentForm({...paymentForm, advance_percent: parseInt(e.target.value) || 0})}
                  min={0}
                  max={100}
                />
              </div>
              <div className="form-group">
                <Label>Discount Percent (%)</Label>
                <Input
                  type="number"
                  required
                  value={paymentForm.discount_percent}
                  onChange={(e) => setPaymentForm({...paymentForm, discount_percent: parseFloat(e.target.value) || 0})}
                  min={0}
                  max={100}
                />
              </div>
            </div>
            
            <div className="form-group">
              <Label>Description (optional)</Label>
              <Input
                value={paymentForm.description}
                onChange={(e) => setPaymentForm({...paymentForm, description: e.target.value})}
                placeholder="e.g., Confirms your booking"
              />
            </div>
            
            <div className="form-group">
              <Label>Sort Order</Label>
              <Input
                type="number"
                value={paymentForm.sort_order}
                onChange={(e) => setPaymentForm({...paymentForm, sort_order: parseInt(e.target.value) || 0})}
              />
            </div>
            
            <div className="flex items-center justify-between">
              <Label>Active</Label>
              <Switch
                checked={paymentForm.is_active}
                onCheckedChange={(checked) => setPaymentForm({...paymentForm, is_active: checked})}
              />
            </div>
            
            <div className="flex gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowPaymentModal(false)} className="flex-1">
                Cancel
              </Button>
              <Button type="submit" className="flex-1 bg-[#B08968] hover:bg-[#9A7558] text-white">
                {editingPaymentMode ? "Update" : "Create"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Food Tier Modal */}
      <Dialog open={showFoodModal} onOpenChange={setShowFoodModal}>
        <DialogContent data-testid="food-tier-modal">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">
              {editingFoodTier ? "Edit Food Tier" : "Add Food Tier"}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={saveFoodTier} className="space-y-4 mt-4">
            <div className="form-group">
              <Label>Tier Name</Label>
              <Input
                required
                value={foodForm.name}
                onChange={(e) => setFoodForm({...foodForm, name: e.target.value})}
                placeholder="e.g., 50 Pax Package"
              />
            </div>
            
            <div className="form-group">
              <Label>Minimum Guarantee (pax)</Label>
              <Input
                type="number"
                required
                value={foodForm.min_guarantee}
                onChange={(e) => setFoodForm({...foodForm, min_guarantee: parseInt(e.target.value) || 0})}
                min={1}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="form-group">
                <Label>Base Rate (₹/head/day)</Label>
                <Input
                  type="number"
                  required
                  value={foodForm.base_rate}
                  onChange={(e) => setFoodForm({...foodForm, base_rate: parseFloat(e.target.value) || 0})}
                  min={0}
                />
              </div>
              <div className="form-group">
                <Label>Additional Rate (₹/head/day)</Label>
                <Input
                  type="number"
                  required
                  value={foodForm.additional_rate}
                  onChange={(e) => setFoodForm({...foodForm, additional_rate: parseFloat(e.target.value) || 0})}
                  min={0}
                />
              </div>
            </div>
            
            <div className="form-group">
              <Label>Sort Order</Label>
              <Input
                type="number"
                value={foodForm.sort_order}
                onChange={(e) => setFoodForm({...foodForm, sort_order: parseInt(e.target.value) || 0})}
              />
            </div>
            
            <div className="flex items-center justify-between">
              <Label>Active</Label>
              <Switch
                checked={foodForm.is_active}
                onCheckedChange={(checked) => setFoodForm({...foodForm, is_active: checked})}
              />
            </div>
            
            <div className="flex gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowFoodModal(false)} className="flex-1">
                Cancel
              </Button>
              <Button type="submit" className="flex-1 bg-[#B08968] hover:bg-[#9A7558] text-white">
                {editingFoodTier ? "Update" : "Create"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Decor Category Modal */}
      <Dialog open={showCategoryModal} onOpenChange={setShowCategoryModal}>
        <DialogContent data-testid="decor-category-modal">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">
              {editingCategory ? "Edit Category" : "Add Category"}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={saveCategory} className="space-y-4 mt-4">
            <div className="form-group">
              <Label>Category Name</Label>
              <Input
                required
                value={categoryForm.name}
                onChange={(e) => setCategoryForm({...categoryForm, name: e.target.value})}
                placeholder="e.g., Entrance Gate Arch"
              />
            </div>
            
            <div className="form-group">
              <Label>Sort Order</Label>
              <Input
                type="number"
                value={categoryForm.sort_order}
                onChange={(e) => setCategoryForm({...categoryForm, sort_order: parseInt(e.target.value) || 0})}
              />
            </div>
            
            <div className="flex items-center justify-between">
              <Label>Active</Label>
              <Switch
                checked={categoryForm.is_active}
                onCheckedChange={(checked) => setCategoryForm({...categoryForm, is_active: checked})}
              />
            </div>
            
            <div className="flex gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowCategoryModal(false)} className="flex-1">
                Cancel
              </Button>
              <Button type="submit" className="flex-1 bg-[#B08968] hover:bg-[#9A7558] text-white">
                {editingCategory ? "Update" : "Create"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Decor Item Modal */}
      <Dialog open={showDecorItemModal} onOpenChange={setShowDecorItemModal}>
        <DialogContent data-testid="decor-item-modal">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">
              {editingDecorItem ? "Edit Decor Item" : "Add Decor Item"}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={saveDecorItem} className="space-y-4 mt-4">
            <div className="form-group">
              <Label>Category</Label>
              <Select 
                value={decorItemForm.category_id} 
                onValueChange={handleDecorCategoryChange}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a category" />
                </SelectTrigger>
                <SelectContent>
                  {decorCategories.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id}>
                      {cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="form-group">
              <Label>Item Name</Label>
              <Input
                required
                value={decorItemForm.item_name}
                onChange={(e) => setDecorItemForm({...decorItemForm, item_name: e.target.value})}
                placeholder="e.g., Welcome Board with Props"
              />
            </div>
            
            <div className="form-group">
              <Label>Tariff (₹)</Label>
              <Input
                type="number"
                required
                value={decorItemForm.tariff}
                onChange={(e) => setDecorItemForm({...decorItemForm, tariff: parseFloat(e.target.value) || 0})}
                min={0}
              />
            </div>
            
            <div className="form-group">
              <Label>Sort Order</Label>
              <Input
                type="number"
                value={decorItemForm.sort_order}
                onChange={(e) => setDecorItemForm({...decorItemForm, sort_order: parseInt(e.target.value) || 0})}
              />
            </div>
            
            <div className="flex items-center justify-between">
              <Label>Active</Label>
              <Switch
                checked={decorItemForm.is_active}
                onCheckedChange={(checked) => setDecorItemForm({...decorItemForm, is_active: checked})}
              />
            </div>
            
            <div className="flex gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowDecorItemModal(false)} className="flex-1">
                Cancel
              </Button>
              <Button type="submit" className="flex-1 bg-[#B08968] hover:bg-[#9A7558] text-white">
                {editingDecorItem ? "Update" : "Create"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminPricingRules;
